import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LoadingSpinner } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import MatchupPitcherSelector from './MatchupPitcherSelector';
import { useOktaQuery, KeyTakeaway } from '../../../data';
import KeyTakeawayComponent from '../../../components/KeyTakeaway';

const StyledMatchupReportHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;
    > h3 {
        margin-bottom: 0;
    }
    > div {
        display: flex;
        align-items: center;
    }
`;

const KeyTakeaways = ({ isPlayerLoading, player, playerId, playerTeamStartingPitchers }) => {
    const { t: translate } = useTranslation();
    const userPlayer = usePlayerSelector();
    const [matchupPitcher, setMatchupPitcher] = useState();
    const isCatcherViewingOpponentHitters = userPlayer.IsCatcher;

    useEffect(() => {
        if (player) {
            if (isCatcherViewingOpponentHitters) {
                if (playerTeamStartingPitchers?.length) {
                    setMatchupPitcher({
                        ...playerTeamStartingPitchers[0],
                        name: playerTeamStartingPitchers[0].FullName,
                        value: playerTeamStartingPitchers[0].PlayerID,
                    });
                }
            } else {
                setMatchupPitcher(userPlayer);
            }
        }
    }, [player]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data: keyTakeaways, isFetching: loadingKeyTakeaways } = useOktaQuery(
        ['KeyTakeaways', { playerid: playerId }],
        (t) => KeyTakeaway.getByPlayer(t, playerId),
    );

    if (isPlayerLoading) return <LoadingSpinner />;

    return (
        <>
            <StyledMatchupReportHeader>
                <h3>{player.FullName} {translate('sections.opponent.player.key-takeaways')}</h3>
                <div>
                    {isCatcherViewingOpponentHitters && userPlayer && userPlayer.TeamID ? (
                        <>
                            <b className="me-1">{translate('common.select-a-pitcher')}:</b>
                            <MatchupPitcherSelector
                                onSelect={setMatchupPitcher}
                            />
                        </>
                    ) : isCatcherViewingOpponentHitters && <>{translate('sections.opponent.player.logged-in-no-id')}</>}
                </div>
            </StyledMatchupReportHeader>
            {matchupPitcher?.Throws && (
                <>
                    {loadingKeyTakeaways && <LoadingSpinner />}
                    {keyTakeaways?.filter((kt) => kt.ShowToHand === null || kt.ShowToHand === matchupPitcher?.Throws).map((kt) => (
                        <KeyTakeawayComponent key={kt.TakeawayID} takeaway={kt} />
                    ))}
                </>
            )}
        </>
    );
};

KeyTakeaways.propTypes = {
    isPlayerLoading: PropTypes.bool.isRequired,
    player: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
    }),
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    playerTeamStartingPitchers: PropTypes.arrayOf(PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.string.isRequired,
    })),
};
KeyTakeaways.defaultProps = {
    player: null,
    playerTeamStartingPitchers: undefined,
};

export default KeyTakeaways;
